// Whop Checkout Plan IDs
export const WHOP_PLANS = {
  FREE: 'plan_KBpQxcyxNolG0',
  PAID: 'plan_QGK5T7353DQ11',
  TEST: 'plan_DXjWSDqSMrrMA',
  TEST_FREE: 'plan_2XAsUo39q2IIu',
  TEST_PAID: 'plan_DXjWSDqSMrrMA'
};

// Helper function to get the correct plan IDs based on test mode
export const getWhopPlanId = (planType) => {
  // Check for testmode URL parameter
  const urlParams = new URLSearchParams(window.location.search);
  const isTestMode = urlParams.get('testmode') === 'true';

  if (isTestMode) {
    switch (planType) {
      case 'free':
        return WHOP_PLANS.TEST_FREE;
      case 'paid':
        return WHOP_PLANS.TEST_PAID;
      case 'test':
        return WHOP_PLANS.TEST_PAID; // Use test_paid for the test button
      default:
        return WHOP_PLANS.TEST_PAID;
    }
  } else {
    switch (planType) {
      case 'free':
        return WHOP_PLANS.FREE;
      case 'paid':
        return WHOP_PLANS.PAID;
      case 'test':
        return WHOP_PLANS.TEST;
      default:
        return WHOP_PLANS.PAID;
    }
  }
};

// MLB Markets
const MLB_MARKETS = [
  // Mainline Markets
  { key: 'h2h', label: 'Game', name: 'Game', isMainline: true },
  { key: 'totals', label: 'O/U', name: 'Totals', isMainline: true, excludeFromMaster: true },
  { key: 'spreads', label: 'RL', name: 'Run Line', isMainline: true, excludeFromMaster: true },
  
  // Player Markets — Hitting
  { key: 'batter_hits', label: 'H', name: 'Hits' },
  { key: 'batter_singles', label: '1B', name: 'Singles' },
  { key: 'batter_doubles', label: '2B', name: 'Doubles' },
  { key: 'batter_triples', label: '3B', name: 'Triples' },
  { key: 'batter_home_runs', label: 'HR', name: 'Home Runs' },
  { key: 'batter_total_bases', label: 'TB', name: 'Total Bases' },
  // Player Markets — Scoring & Baserunning
  { key: 'batter_rbis', label: 'RBI', name: 'RBIs' },
  { key: 'batter_runs_scored', label: 'R', name: 'Runs' },
  { key: 'batter_hits_runs_rbis', label: 'H+R+R', name: 'Hits + Runs + RBIs' },
  { key: 'batter_stolen_bases', label: 'SB', name: 'Stolen Bases' },
  // Player Markets — Pitching
  { key: 'pitcher_strikeouts', label: 'K', name: 'Strikeouts' },
  { key: 'pitcher_hits_allowed', label: 'Hits', name: 'Hits Allowed' },
  { key: 'pitcher_outs', label: 'Outs', name: 'Outs' }
];

// Basketball Markets (shared by WNBA and NBA)
const BASKETBALL_MARKETS = [
  // Mainline Markets
  { key: 'h2h', label: 'Game', name: 'Game', isMainline: true },
  { key: 'totals', label: 'O/U', name: 'Totals', isMainline: true, excludeFromMaster: true },
  { key: 'spreads', label: '+/-', name: 'Spread', isMainline: true, excludeFromMaster: true },

  // Player Markets
  { key: 'player_points', label: 'PTS', name: 'Points' },
  { key: 'player_rebounds', label: 'REB', name: 'Rebounds' },
  { key: 'player_assists', label: 'AST', name: 'Assists' },
  { key: 'player_threes', label: '3PM', name: 'Threes' },
  { key: 'player_blocks', label: 'BLK', name: 'Blocks' },
  { key: 'player_steals', label: 'STL', name: 'Steals' },
  { key: 'player_first_basket', label: '1st', name: 'First Basket' },
  { key: 'player_first_team_basket', label: '1st Tm', name: 'First Team Basket' },
  { key: 'player_double_double', label: 'DD', name: 'Double Double' },
  { key: 'player_triple_double', label: 'TD', name: 'Triple Double' },
  { key: 'player_points_rebounds_assists', label: 'PRA', name: 'Points + Rebounds + Assists' },
  { key: 'player_points_rebounds', label: 'P+R', name: 'Points + Rebounds' },
  { key: 'player_points_assists', label: 'P+A', name: 'Points + Assists' },
  { key: 'player_rebounds_assists', label: 'R+A', name: 'Rebounds + Assists' }
];

// WNBA Markets (using shared basketball markets)
const WNBA_MARKETS = BASKETBALL_MARKETS;

// NBA Markets (using shared basketball markets)
const NBA_MARKETS = BASKETBALL_MARKETS;

// Soccer Markets
const SOCCER_MARKETS = [
  { key: 'h2h_3_way', label: 'Game', name: 'Game' },
  { key: 'btts', label: 'BTTS', name: 'Both Teams To Score' },
  { key: 'totals_corners', label: 'Corners', name: 'Total Corners' },
  { key: 'team_totals_corners', label: 'Tm Corners', name: 'Team Total Corners' },
  { key: 'player_goal_scorer_anytime', label: 'Goal', name: 'Anytime Goal Scorer' },
  { key: 'player_shots', label: 'SHO', name: 'Shots' },
  { key: 'player_assists', label: 'AST', name: 'Assists' },
  { key: 'player_shots_on_target', label: 'SOT', name: 'Shots on Target' },
  { key: 'player_first_goal_scorer', label: '1st Goal', name: 'First Goal Scorer' },
  { key: 'player_last_goal_scorer', label: 'Last Goal', name: 'Last Goal Scorer' }
];

// NCAAF Markets (mainline markets matching NFL pattern)
const NCAAF_MARKETS = [
  // Mainline Markets
  { key: 'h2h', label: 'Game', name: 'Game', isMainline: true },
  { key: 'totals', label: 'O/U', name: 'Totals', isMainline: true, excludeFromMaster: true },
  { key: 'spreads', label: '+/-', name: 'Spread', isMainline: true, excludeFromMaster: true },
  { key: 'h2h_halves', label: 'Halves', name: 'Halves', isMainline: true, excludeFromMaster: true },
  { key: 'h2h_quarters', label: 'Quarters', name: 'Quarters', isMainline: true, excludeFromMaster: true }
];

// NFL Markets
const NFL_MARKETS = [
  // Mainline Markets
  { key: 'h2h', label: 'Game', name: 'Game', isMainline: true },
  { key: 'totals', label: 'O/U', name: 'Totals', isMainline: true, excludeFromMaster: true },
  { key: 'spreads', label: '+/-', name: 'Spread', isMainline: true, excludeFromMaster: true },

  // Player Markets - Touchdowns
  { key: 'player_1st_td', label: '1st', name: 'First Touchdown' },
  { key: 'player_last_td', label: 'Last', name: 'Last Touchdown' },
  { key: 'player_tds_over', label: 'TDS', name: 'Total Touchdowns' },

  // Player Markets - Passing
  { key: 'player_pass_tds', label: 'PaTDS', name: 'Pass Touchdowns' },
  { key: 'player_pass_yds', label: 'PaYds', name: 'Pass Yards' },

  // Player Markets - Rushing
  { key: 'player_rush_yds', label: 'RuYds', name: 'Rush Yards' },
  { key: 'player_rush_attempts', label: 'RuAtt', name: 'Rush Attempts' },

  // Player Markets - Receiving
  { key: 'player_receptions', label: 'REC', name: 'Receptions' },
  { key: 'player_reception_yds', label: 'ReYds', name: 'Reception Yards' },

  // Player Markets - Combo
  { key: 'player_rush_reception_yds', label: 'Ru+RecYds', name: 'Rush + Rec Yards' },

  // Player Markets - Kicking
  { key: 'player_field_goals', label: 'FG', name: 'Field Goals Made' }
];

// NHL Markets
const NHL_MARKETS = [
  // Mainline Markets
  { key: 'h2h', label: 'Game', name: 'Game', isMainline: true },
  { key: 'h2h_periods', label: 'Periods', name: 'Periods', isMainline: true, excludeFromMaster: true },
  { key: 'h2h_all', label: 'All', name: 'All', isMainline: true, excludeFromMaster: true },

  // Player Markets
  { key: 'player_goal_scorer_first', label: '1st', name: 'First Goal Scorer' },
  { key: 'player_goal_scorer_last', label: 'Last', name: 'Last Goal Scorer' },
  { key: 'player_goals', label: 'G', name: 'Goals' },
  { key: 'player_points', label: 'Pts', name: 'Points' },
  { key: 'player_assists', label: 'A', name: 'Assists' },
  { key: 'player_shots_on_goal', label: 'SOG', name: 'Shots on Goal' },
  { key: 'player_total_saves', label: 'Sv', name: 'Saves' }
];

// Sport-specific market mapping
export const SPORT_MARKETS = {
  mlb: MLB_MARKETS,
  nba: NBA_MARKETS,
  wnba: WNBA_MARKETS,
  soccer: SOCCER_MARKETS,
  ncaaf: NCAAF_MARKETS,
  nfl: NFL_MARKETS,
  nhl: NHL_MARKETS
};

// Default markets for backward compatibility (MLB)
export const AVAILABLE_MARKETS = MLB_MARKETS;

export const BOOKMAKERS_LIST = [
  'fanduel', 'draftkings', 'betmgm', 'fanatics', 'espnbet',
  'betrivers', 'ballybet', 'bovada', 'caesars', 'hardrockbet', 'bet365', 'pinnacle', 'circa', 'kalshi',
  'prophetx', 'novig', 'fliff'
];

// Books that ship visible but disabled by default — users must opt-in via
// Settings. Used by BooksContext when merging BOOKMAKERS_LIST into a fresh
// enabledBooks state (no saved preference yet).
export const DEFAULT_OFF_BOOKS = new Set(['prophetx', 'novig', 'fliff']);

// Map API bookmaker names to display names
export const BOOKMAKER_ALIASES = {
  'williamhill_us': 'caesars'
};

// Books that can never be the *primary* best book in a frontend recalc.
// Visible exclusions (e.g. circa, kalshi) may still earn a secondary highlight
// when their price ties or beats the primary best — see GamePanel getCellStyle.
export const EXCLUDED_FROM_BESTBOOK = new Set([
  'novig', 'prophetx', 'kalshi', 'polymarket', 'circa', 'fliff', 'pinnacle',
  'hardrockbet_oh', 'hardrockbet_az', 'hardrockbet_fl',
]);
export const isExcludedFromBestBook = (book) =>
  EXCLUDED_FROM_BESTBOOK.has(String(book || '').toLowerCase());

// Default markets by sport
export const DEFAULT_MARKETS = {
  mlb: 'batter_home_runs',
  nba: 'player_first_basket',
  wnba: 'player_points',
  soccer: 'player_goal_scorer_anytime',
  ncaaf: 'h2h',
  ncaab: 'h2h',
  nfl: 'player_1st_td',
  nhl: 'player_goal_scorer_first'
};

// Backward compatibility
export const DEFAULT_MARKET = 'batter_doubles';

/**
 * Get markets for a specific sport
 * @param {string} sport - Sport key ('mlb', 'wnba')
 * @returns {Array} Array of market objects
 */
export const getMarketsForSport = (sport) => {
  return SPORT_MARKETS[sport?.toLowerCase()] || MLB_MARKETS;
};

/**
 * Get default market for a specific sport
 * @param {string} sport - Sport key ('mlb', 'wnba')
 * @returns {string} Default market key
 */
export const getDefaultMarketForSport = (sport) => {
  return DEFAULT_MARKETS[sport?.toLowerCase()] || DEFAULT_MARKETS.mlb;
};

/**
 * Check if a game status indicates the game is finished
 * @param {string} status - Game status
 * @returns {boolean} True if game is finished
 */
export const isGameFinished = (status) => {
  if (!status) return false;
  const upperStatus = status.toUpperCase();
  return status === 'Final' ||
         status === 'Game Over' ||
         upperStatus === 'STATUS_FINAL' ||
         upperStatus === 'STATUS_FULL_TIME' ||
         upperStatus === 'STATUS_CANCELED' ||
         upperStatus === 'STATUS_POSTPONED' ||
         upperStatus.includes('FINAL') ||
         upperStatus.includes('FULL_TIME') ||
         upperStatus.includes('FULL TIME') ||
         upperStatus.includes('CANCELED') ||
         upperStatus.includes('POSTPONED');
};

/**
 * Check if a game status indicates the game is live/in progress
 * @param {string} status - Game status
 * @returns {boolean} True if game is live
 */
export const isGameLive = (status) => {
  if (!status) return false;
  const upperStatus = status.toUpperCase();
  return status === 'Live' ||
         status === 'In Progress' ||
         status === 'STATUS_IN_PROGRESS' ||
         status === 'STATUS_HALFTIME' ||
         status === 'STATUS_END_PERIOD' ||
         upperStatus.includes('IN_PROGRESS') ||
         upperStatus.includes('HALFTIME') ||
         upperStatus.includes('END_PERIOD') ||
         upperStatus.includes('LIVE') ||
         // Soccer-specific live statuses
         upperStatus.includes('FIRST HALF') ||
         upperStatus.includes('SECOND HALF') ||
         upperStatus.includes('EXTRA TIME') ||
         upperStatus.includes('PENALTY') ||
         upperStatus === '1H' ||
         upperStatus === '2H' ||
         upperStatus === 'HT' ||
         upperStatus === 'ET' ||
         upperStatus === 'P';
};

// MLB Ballpark metadata — keyed by home team abbreviation
// cfBearing = compass bearing from home plate to center field
export const BALLPARK_DATA = {
  NYY: { cfBearing: 75, dome: false },
  BOS: { cfBearing: 200, dome: false },
  CHC: { cfBearing: 23, dome: false },
  COL: { cfBearing: 75, dome: false },
  PHI: { cfBearing: 60, dome: false },
  CIN: { cfBearing: 45, dome: false },
  CWS: { cfBearing: 230, dome: false },
  BAL: { cfBearing: 55, dome: false },
  CLE: { cfBearing: 170, dome: false },
  DET: { cfBearing: 115, dome: false },
  KC: { cfBearing: 10, dome: false },
  MIN: { cfBearing: 210, dome: false },
  STL: { cfBearing: 180, dome: false },
  NYM: { cfBearing: 72, dome: false },
  SF: { cfBearing: 30, dome: false },
  SD: { cfBearing: 65, dome: false },
  LAD: { cfBearing: 0, dome: false },
  LAA: { cfBearing: 65, dome: false },
  OAK: { cfBearing: 120, dome: false },
  PIT: { cfBearing: 55, dome: false },
  WSH: { cfBearing: 335, dome: false },
  WAS: { cfBearing: 335, dome: false },
  ATL: { cfBearing: 155, dome: false },
  TOR: { cfBearing: 170, dome: true },
  TEX: { cfBearing: 195, dome: true },
  HOU: { cfBearing: 347, dome: true },
  ARI: { cfBearing: 5, dome: true },
  AZ: { cfBearing: 5, dome: true },
  SEA: { cfBearing: 185, dome: true },
  MIL: { cfBearing: 180, dome: true },
  MIA: { cfBearing: 10, dome: true },
  TB: { cfBearing: 315, dome: true },
  SAC: { cfBearing: 120, dome: false },
};